import type { WeatherNow } from '@shared/types';

// Visual "infographic" weather card: big current temp, hero condition icon,
// 12-hour temp/rain strip, sunrise/sunset row, and a wind/humidity/UV row.
// Data comes from the /api/weather endpoint (Open-Meteo backed).

const WMO_ICON: Record<number, 'sun' | 'partly' | 'cloud' | 'fog' | 'drizzle' | 'rain' | 'storm' | 'snow'> = {
  0: 'sun',
  1: 'sun',
  2: 'partly',
  3: 'cloud',
  45: 'fog',
  48: 'fog',
  51: 'drizzle',
  53: 'drizzle',
  55: 'drizzle',
  61: 'rain',
  63: 'rain',
  65: 'rain',
  71: 'snow',
  73: 'snow',
  75: 'snow',
  80: 'rain',
  81: 'rain',
  82: 'rain',
  95: 'storm',
  96: 'storm',
  99: 'storm',
};

function iconFor(code: number) {
  return WMO_ICON[code] ?? 'partly';
}

function ConditionIcon({ code, size = 64 }: { code: number; size?: number }) {
  const kind = iconFor(code);
  const stroke = 'currentColor';
  const common = { width: size, height: size, viewBox: '0 0 64 64', fill: 'none', stroke, strokeWidth: 3, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (kind) {
    case 'sun':
      return (
        <svg {...common} aria-hidden>
          <circle cx="32" cy="32" r="10" fill="var(--color-sun)" stroke="none" />
          <g stroke="var(--color-sun)">
            {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
              <line
                key={deg}
                x1={32 + Math.cos((deg * Math.PI) / 180) * 16}
                y1={32 + Math.sin((deg * Math.PI) / 180) * 16}
                x2={32 + Math.cos((deg * Math.PI) / 180) * 24}
                y2={32 + Math.sin((deg * Math.PI) / 180) * 24}
              />
            ))}
          </g>
        </svg>
      );
    case 'partly':
      return (
        <svg {...common} aria-hidden>
          <circle cx="24" cy="24" r="8" fill="var(--color-sun)" stroke="none" />
          <path d="M18 44 h24 a8 8 0 0 0 0-16 a10 10 0 0 0-19 3 a7 7 0 0 0-5 13 z" fill="var(--color-surface)" stroke="var(--color-ink-muted)" />
        </svg>
      );
    case 'cloud':
      return (
        <svg {...common} aria-hidden>
          <path d="M16 44 h32 a10 10 0 0 0 0-20 a12 12 0 0 0-23 4 a8 8 0 0 0-9 16 z" fill="var(--color-surface)" stroke="var(--color-ink-muted)" />
        </svg>
      );
    case 'fog':
      return (
        <svg {...common} aria-hidden>
          <path d="M16 38 h32 a10 10 0 0 0 0-20 a12 12 0 0 0-23 4 a8 8 0 0 0-9 16 z" fill="var(--color-surface)" stroke="var(--color-ink-muted)" />
          <line x1="14" y1="48" x2="50" y2="48" stroke="var(--color-ink-muted)" />
          <line x1="18" y1="54" x2="46" y2="54" stroke="var(--color-ink-muted)" />
        </svg>
      );
    case 'drizzle':
    case 'rain':
      return (
        <svg {...common} aria-hidden>
          <path d="M16 36 h32 a10 10 0 0 0 0-20 a12 12 0 0 0-23 4 a8 8 0 0 0-9 16 z" fill="var(--color-surface)" stroke="var(--color-ink-muted)" />
          {[22, 32, 42].map((x) => (
            <line key={x} x1={x} y1={44} x2={x - 2} y2={54} stroke="var(--color-sky)" />
          ))}
        </svg>
      );
    case 'storm':
      return (
        <svg {...common} aria-hidden>
          <path d="M16 34 h32 a10 10 0 0 0 0-20 a12 12 0 0 0-23 4 a8 8 0 0 0-9 16 z" fill="var(--color-surface)" stroke="var(--color-ink-muted)" />
          <path d="M30 40 l-6 10 h6 l-4 10 l12-14 h-6 l4-6 z" fill="var(--color-sun)" stroke="none" />
        </svg>
      );
    case 'snow':
      return (
        <svg {...common} aria-hidden>
          <path d="M16 36 h32 a10 10 0 0 0 0-20 a12 12 0 0 0-23 4 a8 8 0 0 0-9 16 z" fill="var(--color-surface)" stroke="var(--color-ink-muted)" />
          {[22, 32, 42].map((x) => (
            <text key={x} x={x} y="56" fontSize="10" fill="var(--color-sky)" stroke="none" textAnchor="middle">
              *
            </text>
          ))}
        </svg>
      );
  }
}

function formatHour(iso: string): string {
  // '2026-09-02T15:00' -> '3p'
  const t = iso.split('T')[1] ?? '';
  const hourStr = t.split(':')[0];
  if (!hourStr) return '';
  const h = parseInt(hourStr, 10);
  if (isNaN(h)) return '';
  if (h === 0) return '12a';
  if (h < 12) return `${h}a`;
  if (h === 12) return '12p';
  return `${h - 12}p`;
}

function formatClock(iso: string): string {
  const t = iso.split('T')[1] ?? '';
  const [hs, ms] = t.split(':');
  const h = parseInt(hs, 10);
  const m = parseInt(ms, 10);
  if (isNaN(h) || isNaN(m)) return '';
  const period = h >= 12 ? 'PM' : 'AM';
  const hh = h % 12 || 12;
  const mm = String(m).padStart(2, '0');
  return `${hh}:${mm} ${period}`;
}

function uvLabel(uv: number): { label: string; color: string } {
  if (uv >= 8) return { label: 'Very high', color: 'var(--color-coral)' };
  if (uv >= 6) return { label: 'High', color: 'var(--color-sun)' };
  if (uv >= 3) return { label: 'Moderate', color: 'var(--color-primary)' };
  return { label: 'Low', color: 'var(--color-success)' };
}

function compassArrow(direction: number, size = 14) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" style={{ transform: `rotate(${direction}deg)` }} aria-hidden>
      <path d="M10 3 L14 15 L10 12 L6 15 Z" fill="currentColor" />
    </svg>
  );
}

export function WeatherInfographic({ weather }: { weather: WeatherNow }) {
  const uv = uvLabel(weather.uvIndex);
  const hourly = weather.hourly ?? [];
  const maxTemp = Math.max(...hourly.map((h) => h.temperatureF), weather.highF);
  const minTemp = Math.min(...hourly.map((h) => h.temperatureF), weather.lowF);
  const range = Math.max(1, maxTemp - minTemp);

  return (
    <div className="weather-info card" role="group" aria-label="Current weather and forecast">
      {/* Top: big temp + condition icon + high/low + feels-like */}
      <div className="weather-info__top">
        <div className="weather-info__temp">
          <div className="weather-info__temp-value">
            {weather.temperatureF}
            <span className="weather-info__temp-unit">°F</span>
          </div>
          <div className="weather-info__temp-meta">
            <div className="weather-info__conditions">{weather.conditions}</div>
            <div className="weather-info__feels">Feels like {weather.feelsLikeF}°</div>
            <div className="weather-info__hilo">
              H {weather.highF}° · L {weather.lowF}°
            </div>
          </div>
        </div>
        <div className="weather-info__icon" style={{ color: 'var(--color-ink-muted)' }}>
          <ConditionIcon code={weather.weatherCode} size={72} />
        </div>
      </div>

      {/* Severe alerts are deliberately NOT rendered here. Home's compact
          alert strip (pickCriticalHomeAlerts) already raises a one-line
          cue for every non-empty severeAlert. Rendering the NWS sentence
          again here would print it twice on one screen. Full prep / shelter
          actions live on /prepare and /emergency. */}

      {/* Hourly strip */}
      {hourly.length > 0 && (
        <div className="weather-info__hourly" aria-label="12-hour forecast">
          {hourly.map((h, i) => {
            const heightPct = ((h.temperatureF - minTemp) / range) * 60 + 20; // 20..80
            return (
              <div key={i} className="weather-info__hour">
                <div className="weather-info__hour-temp">{h.temperatureF}°</div>
                <div className="weather-info__hour-bar-wrap" title={`${h.temperatureF}° · ${h.precipProbability}% rain`}>
                  <div
                    className="weather-info__hour-bar"
                    style={{
                      height: `${heightPct}%`,
                      background: `linear-gradient(180deg, var(--color-sky) 0%, var(--color-primary) 100%)`,
                    }}
                  />
                  {h.precipProbability >= 30 && (
                    <div className="weather-info__hour-rain" style={{ opacity: Math.min(1, h.precipProbability / 100) }}>
                      {h.precipProbability}%
                    </div>
                  )}
                </div>
                <div className="weather-info__hour-time">{formatHour(h.time)}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sunrise / sunset */}
      {(weather.sunrise || weather.sunset) && (
        <div className="weather-info__sun-row">
          <div className="weather-info__sun-item">
            <span className="weather-info__sun-icon" aria-hidden style={{ color: 'var(--color-sun)' }}>
              ↑
            </span>
            <div>
              <div className="weather-info__sun-label">Sunrise</div>
              <div className="weather-info__sun-time">{formatClock(weather.sunrise)}</div>
            </div>
          </div>
          <div className="weather-info__sun-item">
            <span className="weather-info__sun-icon" aria-hidden style={{ color: 'var(--color-coral)' }}>
              ↓
            </span>
            <div>
              <div className="weather-info__sun-label">Sunset</div>
              <div className="weather-info__sun-time">{formatClock(weather.sunset)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Wind / humidity / UV */}
      <div className="weather-info__stats">
        <div className="weather-info__stat">
          <span className="weather-info__stat-icon" style={{ color: 'var(--color-primary)' }}>
            {compassArrow(weather.windDirection)}
          </span>
          <div>
            <div className="weather-info__stat-value">{weather.windMph} mph</div>
            <div className="weather-info__stat-label">Wind</div>
          </div>
        </div>
        <div className="weather-info__stat">
          <span className="weather-info__stat-icon" aria-hidden style={{ color: 'var(--color-sky)' }}>
            💧
          </span>
          <div>
            <div className="weather-info__stat-value">{weather.humidity}%</div>
            <div className="weather-info__stat-label">Humidity</div>
          </div>
        </div>
        <div className="weather-info__stat">
          <span className="weather-info__stat-uv" style={{ background: uv.color }}>
            {weather.uvIndex}
          </span>
          <div>
            <div className="weather-info__stat-value">{uv.label}</div>
            <div className="weather-info__stat-label">UV Index</div>
          </div>
        </div>
      </div>
    </div>
  );
}
